import { useState, useEffect } from "react";
import {
    Grid,
    Box,
    Tab,
    Tabs,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Snackbar,
    Alert,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    ButtonGroup
} from "@mui/material";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTheme } from '@mui/material/styles';
import LoadingSwitch from "../../components/LoadingSwitch";
import MainCard from "../../components/cards/MainCard";
import {
    useUpdateTenancyMutation,
    useGetTenancyByIdQuery,
    useGetFeatureSwitchesQuery,
    useUpdateFeatureSwitchesMutation,
    tenancyApi
} from "../../store/services/tenancy.service";
import { useSelector, useDispatch } from "react-redux";
import { getCardColor } from '../../utils/colorUtils';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/WidgetsRounded';
import FeedIcon from '@mui/icons-material/Feed';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { ReactComponent as PacksIcon }    from '../../assets/navigation-icons/packs.svg';
import { ReactComponent as SettingsIcon } from '../../assets/navigation-icons/settings.svg';
import { ReactComponent as ReportIcon }   from '../../assets/navigation-icons/report.svg';
import { ReactComponent as TenantsIcon }  from '../../assets/navigation-icons/tenants.svg';
import { ReactComponent as HelpIcon }     from '../../assets/navigation-icons/help.svg';
import { ReactComponent as TrillionIcon } from '../../assets/navigation-icons/trillion.svg';
import {
    FEATURE_DEFAULTS as REPORTS_B_FEATURE_DEFAULTS,
    FEATURE_META as REPORTS_B_FEATURE_META,
    FEATURES as REPORTS_B_FEATURES,
    setFeature as setReportsBFeature,
    subscribeFeatures as subscribeReportsBFeatures,
} from "../reports-b/featureFlags";
import { WIDGET_SECTIONS } from "../reports-b/widgetRegistry";

// reports-b widget visibility lives in localStorage keyed by tenancy +
// page. Features page reads/writes the same keys so admin toggles here
// affect every user in the tenancy. Mirrors the key shape used by
// useWidgetVisibility.js so the customise drawer and Features page stay
// in sync.
const REPORTS_B_HIDDEN_PREFIX = 'rbx-hidden-cards';
function reportsBHiddenKey(tenancyId, page) {
    return `${REPORTS_B_HIDDEN_PREFIX}:${tenancyId || 'default'}:${page}`;
}
function loadReportsBHidden(tenancyId, page) {
    try { return JSON.parse(localStorage.getItem(reportsBHiddenKey(tenancyId, page))) || []; }
    catch { return []; }
}
function saveReportsBHidden(tenancyId, page, ids) {
    localStorage.setItem(reportsBHiddenKey(tenancyId, page), JSON.stringify(ids));
}

// Licence tier presets. Mirrors the published tiers on boxphish.com/pricing
// collapsed to two options:
//   - Platform: LITE + ESSENTIALS features (everything except the
//     ENHANCED add-ons).
//   - Enhanced: LITE + ESSENTIALS + ENHANCED (full product).
// Maps only to existing switches; no new feature ids are introduced.
// `api` keys flow through the tenancy feature-switch API; `reportsB`
// keys flow through the reports-b feature-flag pub-sub. Anything not
// listed is assumed to keep its current value (e.g. enableTenants is
// admin-only and never set by a customer licence).
const LICENCE_PRESETS = {
    platform: {
        label: 'Essentials',
        description: 'Lite + Essentials. Phishing sims, courses, analytics, branding, Teams and Microsoft/Google integrations, reminders and exec summaries.',
        api: {
            enableNewsFeed: true,
            enableDashboard: true,
            enableDashboardCourseOverview: true,
            enableDashboardActivityTimeline: true,
            enableDashboardUserSearch: true,
            enableDashboardMostClickedSimulations: true,
            enableDashboardDepartmentOverview: true,
            enableContent: true,
            enableOndemand: true,
            enableSimulation: true,
            enableCourse: true,
            enableJourneyBuilder: true,
            enableJourneyBuilderCourses: true,
            enableSettings: true,
            enableSettingsGeneral: true,
            enableReminders: true,
            enableNotifications: true,
            enableTeamsUI: true,
            enableEmailUI: true,
            enableSettingsDeliverability: true,
            enableSendingInfo: true,
            enableMicrosoftIntegration: true,
            enableGoogleIntegration: true,
            enableSettingsBranding: true,
            enableBrandingTheme: true,
            enableBrandingEmails: true,
            enableBrandingLandingPage: true,
            enableSettingsCompanyInfo: true,
            enableSettingsPhishReportButton: true,
            enableTeamsIntegration: true,
            enableReporting: true,
            enableHelp: true,
            // Enhanced-tier add-ons: off
            enableAddSimulation: false,
            enableRandomSimulation: false,
            enableJourneyBuilderSimulations: false,
            enableAddCourse: false,
            enableContentPacks: false,
            enableLineManagerEscalation: false,
            enablePauseJourneys: false,
            enableCustomEmailHeaders: false,
            enableCustomEmailSubdomain: false,
            enableGateways: false,
            enableBrandingReportedSimEmails: false,
            enableDmi: false,
            enableReportingApi: false,
            showTrillion: false,
            enableTrillion: false,
        },
        reportsB: {
            industryBenchmarking: false,   // Enhanced add-on
            darkWebMonitoring: false,      // Enhanced add-on
            flaggedSimulations: false,
            exportBuilder: true,           // Lite includes CSV/Excel/PDF export
        },
    },
    enhanced: {
        label: 'Enhanced',
        description: 'Everything in Platform plus automated sim journeys, customisation, content uploads, Phish Report integration, industry benchmarking, dark web monitoring, API datafeed and custom sending domains.',
        api: {
            enableNewsFeed: true,
            enableDashboard: true,
            enableDashboardCourseOverview: true,
            enableDashboardActivityTimeline: true,
            enableDashboardUserSearch: true,
            enableDashboardMostClickedSimulations: true,
            enableDashboardDepartmentOverview: true,
            enableContent: true,
            enableOndemand: true,
            enableSimulation: true,
            enableAddSimulation: true,
            enableRandomSimulation: true,
            enableCourse: true,
            enableAddCourse: true,
            enableContentPacks: true,
            enableJourneyBuilder: true,
            enableJourneyBuilderSimulations: true,
            enableJourneyBuilderCourses: true,
            enableSettings: true,
            enableSettingsGeneral: true,
            enableReminders: true,
            enableLineManagerEscalation: true,
            enablePauseJourneys: true,
            enableNotifications: true,
            enableTeamsUI: true,
            enableEmailUI: true,
            enableCustomEmailHeaders: true,
            enableCustomEmailSubdomain: true,
            enableSettingsDeliverability: true,
            enableSendingInfo: true,
            enableMicrosoftIntegration: true,
            enableGoogleIntegration: true,
            enableGateways: true,
            enableDmi: true,
            enableSettingsBranding: true,
            enableBrandingTheme: true,
            enableBrandingEmails: true,
            enableBrandingReportedSimEmails: true,
            enableBrandingLandingPage: true,
            enableSettingsCompanyInfo: true,
            enableSettingsPhishReportButton: true,
            enableTeamsIntegration: true,
            enableReporting: true,
            enableReportingApi: true,
            enableHelp: true,
            showTrillion: true,
            enableTrillion: true,
        },
        reportsB: {
            industryBenchmarking: true,
            darkWebMonitoring: true,
            flaggedSimulations: true,
            exportBuilder: true,
        },
    },
};

const LICENCE_STORAGE_KEY = 'rbx-licence-tier';
function loadLicenceTier(tenancyId) {
    try { return localStorage.getItem(`${LICENCE_STORAGE_KEY}:${tenancyId || 'default'}`); }
    catch { return null; }
}
function saveLicenceTier(tenancyId, tier) {
    try { localStorage.setItem(`${LICENCE_STORAGE_KEY}:${tenancyId || 'default'}`, tier); } catch { /* ignore */ }
}

// reports-b sub-page toggles. Master switch is `enableReporting` (the
// existing API-backed flag that gates the left nav). These are the
// sub-pages within Reports B, each gated by a reports-b localStorage
// flag so the customer admin can turn an individual page off without
// hiding the whole product surface.
const REPORTS_B_SUBPAGES = [
    { id: 'industryBenchmarking', label: 'Industry benchmarking',     description: 'Compare to anonymised peer averages on every risk factor. Off hides all "vs industry" anchors.' },
    { id: 'darkWebMonitoring',    label: 'Dark web monitoring page',  description: 'Removes the Dark web page and its breach data.' },
    { id: 'flaggedSimulations',   label: 'Flagged simulations page',  description: 'Removes the dedicated flagged-sims page.' },
    { id: 'exportBuilder',        label: 'Export builder page',       description: 'Removes the Export builder page; per-table exports stay available.' },
];

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`features-tabpanel-${index}`}
            aria-labelledby={`features-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `features-tab-${index}`,
        'aria-controls': `features-tabpanel-${index}`,
    };
}

const FeaturesDefault = () => {
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';

    const [tabValue, setTabValue] = useState(0);
    const [preset, setPreset] = useState('default');
    const [, forceReportsBRerender] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedAreaId, setSelectedAreaId] = useState('dashboard');

    // Get tenancy ID from Redux store and setup dispatch
    const { tenancyId } = useSelector((state) => state.tenancy);
    const dispatch = useDispatch();

    const [licenceTier, setLicenceTier] = useState(() => loadLicenceTier(tenancyId) || 'platform');
    const [pendingTier, setPendingTier] = useState(null); // tier waiting for confirmation
    useEffect(() => {
        const stored = loadLicenceTier(tenancyId);
        if (stored && stored !== licenceTier) setLicenceTier(stored);
    }, [tenancyId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Re-render when reports-b feature flags (localStorage) change so
    // toggles in this tab and toggles in the reports-b topbar stay in sync.
    useEffect(() => subscribeReportsBFeatures(() => forceReportsBRerender((n) => n + 1)), []);

    // Reports-b widget hidden state per page, loaded once and mutated
    // through helper callbacks. Mirrors useWidgetVisibility.js so the
    // customise drawer and the Features page write the same key.
    const [reportsBWidgetHidden, setReportsBWidgetHidden] = useState(() => ({
        summary: loadReportsBHidden(tenancyId, 'summary'),
        sims:    loadReportsBHidden(tenancyId, 'sims'),
        courses: loadReportsBHidden(tenancyId, 'courses'),
    }));
    useEffect(() => {
        setReportsBWidgetHidden({
            summary: loadReportsBHidden(tenancyId, 'summary'),
            sims:    loadReportsBHidden(tenancyId, 'sims'),
            courses: loadReportsBHidden(tenancyId, 'courses'),
        });
    }, [tenancyId]);

    const toggleReportsBWidget = (page, widgetId) => {
        setReportsBWidgetHidden((prev) => {
            const list = prev[page] || [];
            const next = list.includes(widgetId) ? list.filter((x) => x !== widgetId) : [...list, widgetId];
            saveReportsBHidden(tenancyId, page, next);
            return { ...prev, [page]: next };
        });
    };
    const isReportsBWidgetOn = (page, widgetId) => !(reportsBWidgetHidden[page] || []).includes(widgetId);

    const toggleReportsBSubpage = (id) => {
        setReportsBFeature(id, !REPORTS_B_FEATURES[id]);
    };

    // Get tenancy data from API
    const {
        data: tenancyData,
        isLoading: tenancyLoading,
        error: tenancyError
    } = useGetTenancyByIdQuery({ tenancyId });

    // Get feature switches data from API
    const {
        data: featureSwitchesApiData,
        isLoading: featureSwitchesLoading,
        error: featureSwitchesError
    } = useGetFeatureSwitchesQuery({ tenancyId });

    // Log feature switches data when it changes
    useEffect(() => {
        if (featureSwitchesApiData) {

            // Check if we need to access the nested data property
            const actualData = featureSwitchesApiData.data || featureSwitchesApiData;
        }
    }, [featureSwitchesApiData]);

    // API mutations
    const [updateTenancy] = useUpdateTenancyMutation();
    const [updateFeatureSwitches] = useUpdateFeatureSwitchesMutation();

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    // Loading states
    const [loadingStates, setLoadingStates] = useState({
        enableNewsFeed: false,
        enableSettings: false,
        enableContent: false,
        enableReporting: false,
        enableTenants: false,
        enableHelp: false,
        enableTrillion: false,
        showTrillion: false,
        enableDashboard: false,
        enableDashboardCourseOverview: false,
        enableDashboardActivityTimeline: false,
        enableDashboardUserSearch: false,
        enableDashboardMostClickedSimulations: false,
        enableDashboardDepartmentOverview: false,
        enableOndemand: false,
        enableSimulation: false,
        enableAddSimulation: false,
        enableCourse: false,
        enableJourneyBuilder: false,
        enableContentPacks: false,
        enableSettingsGeneral: false,
        enableSettingsDeliverability: false,
        enableSettingsBranding: false,
        enableSettingsCompanyInfo: false,
        enableSettingsPhishReportButton: false,
        enableNotifications: false,
        enableMfa: false,
        enableCustomEmailHeaders: false,
        enableCustomEmailSubdomain: false,
        enableReminders: false,
        enableLineManagerEscalation: false,
        enablePauseJourneys: false,
        enableExcelReport: false,
        enableSendingInfo: false,
        enableMicrosoftIntegration: false,
        enableGoogleIntegration: false,
        enableGateways: false,
        enableSafeSenderSync: false,
        enableBrandingTheme: false,
        enableBrandingEmails: false,
        enableBrandingLandingPage: false,
        enableReportingApi: false,
        enableRandomSimulation: false,
        enableJourneyBuilderSimulations: false,
        enableJourneyBuilderCourses: false,
        enableBrandingReportedSimEmails: false,
        enableAddCourse: false,
        enableTeamsIntegration: false,
        enableTeamsUI: false,
        enableEmailUI: false,
        enableDmi: false
    });

    // Navigation Bar Items - Main Tabs
    const NAV_BAR_ITEMS = [
        { key: 'enableNewsFeed', name: 'News Feed', tooltip: 'Enable or disable News Feed navigation item' },
        { key: 'enableSettings', name: 'Settings', tooltip: 'Enable or disable Settings navigation item' },
        { key: 'enableContent', name: 'Content', tooltip: 'Enable or disable Content navigation item' },
        { key: 'enableReporting', name: 'Reports', tooltip: 'Enable or disable Reports navigation item' },
        { key: 'enableReportsB', name: 'Reports B', tooltip: 'Enable or disable Reports B navigation item (super admin, boxphish only)' },
        { key: 'enableTenants', name: 'Tenants', tooltip: 'Enable or disable Tenants navigation item' },
        { key: 'enableHelp', name: 'Help', tooltip: 'Enable or disable Help navigation item' },
        { key: 'enableDashboard', name: 'Dashboard', tooltip: 'Enable or disable Dashboard navigation item' },
        { key: 'showTrillion', name: 'DWM', tooltip: 'Show/hide Dark Web Monitoring navigation item' }
    ];

    // Feature configuration objects organized by nav bar hierarchy
    const FEATURE_CONFIG = {
        newsFeed: {
            master: 'enableNewsFeed',
            features: []
        },
        settings: {
            master: 'enableSettings',
            secondaryTabs: [
                {
                    name: 'General',
                    master: 'enableSettingsGeneral',
                    features: [
                        { key: 'enableCustomEmailHeaders', name: 'Custom Email Headers', tooltip: 'Enable or disable custom email headers' },
                        { key: 'enableCustomEmailSubdomain', name: 'Custom Email Domain', tooltip: 'Enable or disable custom email domain for sending courses' },
                        { key: 'enableReminders', name: 'Reminders', tooltip: 'Enable or disable email reminders' },
                        { key: 'enableLineManagerEscalation', name: 'Line Manager Escalation', tooltip: 'Enable or disable line manager escalation for reminders' },
                        { key: 'enablePauseJourneys', name: 'Pause Emails', tooltip: 'Enable or disable emails for the tenancy' },
                        { key: 'enableExcelReport', name: 'Excel Report', tooltip: 'Enable or disable the Excel Report section under General settings' },
                        { key: 'enableNotifications', name: 'Course Notifications', tooltip: 'Enable or disable course notifications section in settings' },
                        { key: 'enableTeamsUI', name: 'Teams Notification UI', tooltip: 'Enable or disable Teams notification toggle in course notifications', parentKey: 'enableNotifications' },
                        { key: 'enableEmailUI', name: 'Email Notification UI', tooltip: 'Enable or disable Email notification toggle in course notifications', parentKey: 'enableNotifications' }
                    ]
                },
                {
                    name: 'Deliverability',
                    master: 'enableSettingsDeliverability',
                    features: [
                        { key: 'enableSendingInfo', name: 'Sending Information', tooltip: 'Enable or disable Sending Information tab' },
                        { key: 'enableMicrosoftIntegration', name: 'Microsoft Integration', tooltip: 'Enable or disable Microsoft/Office 365 integration' },
                        { key: 'enableGoogleIntegration', name: 'Google Integration', tooltip: 'Enable or disable Google Workspace integration' },
                        { key: 'enableGateways', name: 'Gateways', tooltip: 'Enable or disable gateway configurations' },
                        { key: 'enableSafeSenderSync', name: 'Safe Sender Sync', tooltip: 'Enable or disable the one-click Safe Sender Sync connect flow' }
                    ]
                },
                {
                    name: 'Branding',
                    master: 'enableSettingsBranding',
                    features: [
                        { key: 'enableBrandingTheme', name: 'Theme Customisation', tooltip: 'Enable or disable theme customization features' },
                        { key: 'enableBrandingEmails', name: 'Custom Email Templates', tooltip: 'Enable or disable email template branding' },
                        { key: 'enableBrandingReportedSimEmails', name: 'Custom PRI Emails', tooltip: 'Enable or disable custom PRI email branding' },
                        { key: 'enableBrandingLandingPage', name: 'Custom Landing Pages', tooltip: 'Enable or disable landing page branding' },
                    { key: 'enableDmi', name: 'Direct Mail Injection', tooltip: 'Enable simulation email injection via Azure Function App and Microsoft Graph API instead of SMTP' }
                    ]
                },
                {
                    name: 'Company Information',
                    master: 'enableSettingsCompanyInfo',
                    features: []
                },
                {
                    name: 'Phish Report Button',
                    master: 'enableSettingsPhishReportButton',
                    features: []
                },
                {
                    name: 'API Integration',
                    features: [
                        { key: 'enableReportingApi', name: 'Reporting API', tooltip: 'Enable or disable Reporting API integration' }
                    ]
                },
                {
                    name: 'Teams Integration',
                    master: 'enableTeamsIntegration',
                    features: []
                }
            ]
        },
        content: {
            master: 'enableContent',
            secondaryTabs: [
                {
                    name: 'On Demand',
                    master: 'enableOndemand',
                    features: [
                        { key: 'enableSimulation', name: 'On Demand Simulations', tooltip: 'Enable or disable simulation features in on-demand' },
                        { key: 'enableAddSimulation', name: 'On Demand Add Simulations', tooltip: 'Enable or disable the ability to create new simulations' },
                        { key: 'enableCourse', name: 'On Demand Courses', tooltip: 'Enable or disable course features in on-demand' }
                    ]
                },
                {
                    name: 'Journey Builder',
                    master: 'enableJourneyBuilder',
                    features: [
                        { key: 'enableJourneyBuilderSimulations', name: 'Journey Builder Simulations', tooltip: 'Enable or disable simulations in Journey Builder' },
                        { key: 'enableJourneyBuilderCourses', name: 'Journey Builder Courses', tooltip: 'Enable or disable courses in Journey Builder' }
                    ]
                },
                {
                    name: 'Content Packs',
                    master: 'enableContentPacks',
                    features: []
                },
                {
                    name: 'Random Simulations',
                    master: 'enableRandomSimulation',
                    features: []
                },
                {
                    name: 'Customer Courses',
                    master: 'enableAddCourse',
                    features: []
                }

            ]
        },
        reports: {
            master: 'enableReporting',
            features: []
        },
        reportsB: {
            master: 'enableReportsB',
            features: []
        },
        tenants: {
            master: 'enableTenants',
            features: []
        },
        help: {
            master: 'enableHelp',
            features: []
        },
        dashboard: {
            master: 'enableDashboard',
            secondaryTabs: [
                {
                    name: 'Dashboard',
                    master: 'enableDashboard',
                    features: [
                        { key: 'enableDashboardCourseOverview', name: 'Your Courses', tooltip: 'Enable or disable Your Courses section on Dashboard' },
                        { key: 'enableDashboardActivityTimeline', name: 'Activity Timeline', tooltip: 'Enable or disable Activity Timeline section on Dashboard' },
                        { key: 'enableDashboardUserSearch', name: 'User Search & Analytics', tooltip: 'Enable or disable User Search and Analytics section on Dashboard' },
                        { key: 'enableDashboardMostClickedSimulations', name: 'Most Clicked Simulations', tooltip: 'Enable or disable Simulation Performance section on Dashboard' },
                        { key: 'enableDashboardDepartmentOverview', name: 'Department Overview', tooltip: 'Enable or disable Department Overview section on Dashboard' }
                    ]
                }
            ]
        },
        trillion: {
            master: 'showTrillion',
             secondaryTabs: [
                {
                    name: 'Show/hide Dark Web Monitoring button',
                    master: 'showTrillion',
                    features: [
                        { key: 'enableTrillion', name: 'Enable/disable Dark Web Monitoring Button', tooltip: 'Controls wether the Trillion button is greyed out' },
                    ]
                }
            ]
        },
    };

    // Special features that don't belong to nav bar items
    const SPECIAL_FEATURES = [
        { key: 'enableCourseRetake', name: 'Enable Course Retake', tooltip: 'Enable users to retake courses', section: 'Course Settings' }
    ];

    // Define theme-aware table styles
    const getTableStyles = () => ({
        tableContainer: {
            backgroundColor: lightMode ? 'inherit' : getCardColor(lightMode),
            borderRadius: theme.borderRadius?.sm || 1,
            border: lightMode ? 'none' : `1px solid ${theme.palette.grey[600]}`,
        },
        table: {
            backgroundColor: 'transparent', // Let container handle background
        },
        tableHead: {
            backgroundColor: lightMode
                ? theme.palette.grey[50]
                : theme.palette.grey[800],
        },
        tableHeaderCell: {
            backgroundColor: 'inherit',
            color: lightMode
                ? theme.palette.text.primary
                : theme.palette.text.primary,
            fontWeight: 600,
            borderBottom: `1px solid ${lightMode
                ? theme.palette.grey[200]
                : theme.palette.grey[700]}`,
        },
        tableBodyCell: {
            borderBottom: `1px solid ${lightMode
                ? theme.palette.grey[200]
                : theme.palette.grey[700]}`,
            color: theme.palette.text.primary,
        },
        tableRow: {
            '&:hover': {
                backgroundColor: lightMode
                    ? theme.palette.action.hover
                    : theme.palette.grey[800],
            },
            '&:last-child td': {
                borderBottom: 'none',
            },
        }
    });

    const tableStyles = getTableStyles();

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        // Reset secondary tab when changing main tabs
        setSecondaryTabValues({
            settings: 0,
            content: 0,
            dashboard: 0
        });
    };

    const handlePresetChange = (event) => {
        setPreset(event.target.value);
    };

    // Secondary tab states
    const [secondaryTabValues, setSecondaryTabValues] = useState({
        settings: 0,
        content: 0,
        dashboard: 0
    });

    const handleSecondaryTabChange = (section, newValue) => {
        setSecondaryTabValues(prev => ({
            ...prev,
            [section]: newValue
        }));
    };

    const setLoadingFor = (setting, isLoading) => {
        setLoadingStates(prev => ({
            ...prev,
            [setting]: isLoading
        }));
    };

    const isAnySwitchLoading = () => {
        return Object.values(loadingStates).some(state => state === true);
    };

    // Get current master switch based on active tab
    const getCurrentMasterSwitch = () => {
        const tabKeys = ['newsFeed', 'settings', 'content', 'reports', 'tenants', 'help', 'dashboard', 'trillion'];
        const currentTabKey = tabKeys[tabValue];
        return FEATURE_CONFIG[currentTabKey]?.master;
    };

    // Get current master switch value
    const getCurrentMasterSwitchValue = () => {
        const masterKey = getCurrentMasterSwitch();
        if (!masterKey || !featureSwitchesApiData) return false;

        const actualData = featureSwitchesApiData?.data || featureSwitchesApiData;
        return actualData[masterKey] || false;
    };

    // Handle master switch change
    const handleMasterSwitchChange = async () => {
        const masterKey = getCurrentMasterSwitch();
        if (!masterKey || !featureSwitchesApiData) return;

        const actualData = featureSwitchesApiData?.data || featureSwitchesApiData;
        const currentValue = actualData[masterKey];
        const newValue = !currentValue;

        setLoadingFor(masterKey, true);

        try {
            await updateFeatureSwitches({
                tenancyId: tenancyId,
                data: {
                    [masterKey]: newValue
                }
            }).unwrap();

            const featureName = NAV_BAR_ITEMS.find(item => item.key === masterKey)?.name || 'Feature';
            setSnackbarMessage(`${featureName} setting updated successfully.`);
            setSnackbarSeverity("success");

            // For parent tenancies, always force a refresh
            if (isParentTenancy()) {
                console.log("Parent toggle changed, forcing refresh");
                dispatch(tenancyApi.util.invalidateTags([
                    { type: "FeatureSwitches", id: "LIST" },
                    { type: "Tenancy", id: "LIST" }
                ]));
            }
        } catch (error) {
            console.error(`Error updating ${masterKey} setting:`, error);

            const featureName = NAV_BAR_ITEMS.find(item => item.key === masterKey)?.name || 'Feature';

            if (error.data && error.data.message) {
                setSnackbarMessage(error.data.message);
            } else {
                setSnackbarMessage(`Error updating ${featureName} setting.`);
            }
            setSnackbarSeverity("error");
        } finally {
            setSnackbarOpen(true);
            setLoadingFor(masterKey, false);
        }
    };

    // Updated FeatureSwitch component with theme-aware styling
    const FeatureSwitch = ({ feature, checked, onToggle, loading, disabled }) => (
        <TableRow
            key={`${feature.key}-row`}
            sx={tableStyles.tableRow}
        >
            <TableCell
                component="th"
                scope="row"
                sx={tableStyles.tableBodyCell}
            >
                <Typography variant="body2">
                    {feature.name}
                </Typography>
            </TableCell>
            <TableCell sx={tableStyles.tableBodyCell}>
                <Box display="flex" alignItems="center">
                    <LoadingSwitch
                        checked={checked}
                        onChange={onToggle}
                        loading={loading}
                        disabled={disabled || isAnySwitchLoading()}
                        inputProps={{ "aria-label": "controlled" }}
                    />
                    {loading && (
                        <Typography variant="caption" color="info.main" sx={{ ml: 1 }}>
                            Updating...
                        </Typography>
                    )}
                </Box>
            </TableCell>
        </TableRow>
    );

    // Determine if this is a parent tenancy (no parentSettings property)
    const isParentTenancy = () => {
        const actualData = featureSwitchesApiData?.data || featureSwitchesApiData;
        const isParent = actualData && actualData.parentSettings === null;

        if (isParent) {
            console.log("Parent tenancy detected:", tenancyId);
            console.log("Using data:", actualData);
        }

        return isParent;
    };

    // Generic function to render feature rows
    const renderFeatureRows = (features) => {
        const rows = [];
        const actualData = featureSwitchesApiData?.data || featureSwitchesApiData;

        features.forEach(feature => {
            rows.push(
                <FeatureSwitch
                    key={feature.key}
                    feature={feature}
                    checked={actualData?.[feature.key] ?? false}
                    onToggle={() => handleGenericFeatureToggle(feature.key)}
                    loading={loadingStates[feature.key]}
                />
            );
        });

        return rows;
    };

    // Render master switch row for secondary tabs
    const renderMasterSwitchRow = (masterKey, name) => {
        if (!masterKey) return null;
        const actualData = featureSwitchesApiData?.data || featureSwitchesApiData;

        return (
            <FeatureSwitch
                key={masterKey}
                feature={{ key: masterKey, name: name }}
                checked={actualData?.[masterKey] ?? false}
                onToggle={() => handleGenericFeatureToggle(masterKey)}
                loading={loadingStates[masterKey]}
            />
        );
    };

    // Updated render function for tables with theme styling
    const renderThemedTable = (content, ariaLabel) => (
        <TableContainer sx={tableStyles.tableContainer}>
            <Table
                sx={{
                    ...tableStyles.table,
                    minWidth: 650,
                    tableLayout: 'fixed'
                }}
                aria-label={ariaLabel}
            >
                <TableHead sx={tableStyles.tableHead}>
                    <TableRow>
                        <TableCell
                            width="75%"
                            sx={tableStyles.tableHeaderCell}
                        >
                            Feature
                        </TableCell>
                        <TableCell
                            width="25%"
                            sx={tableStyles.tableHeaderCell}
                        >
                            On/Off
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {content}
                </TableBody>
            </Table>
        </TableContainer>
    );

    // Helper function to find child features that depend on a parent key
    const getChildFeatures = (parentKey) => {
        const children = [];
        for (const category of Object.values(FEATURE_CONFIG)) {
            if (category.features) {
                category.features.forEach(f => {
                    if (f.parentKey === parentKey) children.push(f.key);
                });
            }
            if (category.secondaryTabs) {
                for (const tab of category.secondaryTabs) {
                    tab.features.forEach(f => {
                        if (f.parentKey === parentKey) children.push(f.key);
                    });
                }
            }
        }
        return children;
    };

    // Generic feature toggle handler
    const handleGenericFeatureToggle = async (featureKey) => {
        if (!featureSwitchesApiData) return;

        const actualData = featureSwitchesApiData?.data || featureSwitchesApiData;
        const currentValue = actualData[featureKey];
        const newValue = !currentValue;

        setLoadingFor(featureKey, true);

        // Build the update data - if turning off a parent, also turn off its children
        const updateData = { [featureKey]: newValue };
        if (!newValue) {
            const childFeatures = getChildFeatures(featureKey);
            childFeatures.forEach(childKey => {
                updateData[childKey] = false;
            });
        }

        try {
            await updateFeatureSwitches({
                tenancyId: tenancyId,
                data: updateData
            }).unwrap();

            // Get feature name for display
            const getFeatureName = (key) => {
                // Check nav bar items
                const navItem = NAV_BAR_ITEMS.find(item => item.key === key);
                if (navItem) return navItem.name;

                // Check feature config
                for (const category of Object.values(FEATURE_CONFIG)) {
                    if (category.features) {
                        const feature = category.features.find(f => f.key === key);
                        if (feature) return feature.name;
                    }
                    if (category.secondaryTabs) {
                        for (const tab of category.secondaryTabs) {
                            if (tab.master === key) return tab.name;
                            const feature = tab.features.find(f => f.key === key);
                            if (feature) return feature.name;
                        }
                    }
                }

                return key;
            };

            const featureName = getFeatureName(featureKey);
            setSnackbarMessage(`${featureName} setting updated successfully.`);
            setSnackbarSeverity("success");

            // For parent tenancies, always force a refresh to ensure UI stays in sync with backend
            if (isParentTenancy()) {
                console.log("Parent toggle changed, forcing refresh");
                dispatch(tenancyApi.util.invalidateTags([
                    { type: "FeatureSwitches", id: "LIST" },
                    { type: "Tenancy", id: "LIST" }
                ]));
            }
        } catch (error) {
            console.error(`Error updating ${featureKey} setting:`, error);

            const getFeatureName = (key) => {
                // Check nav bar items
                const navItem = NAV_BAR_ITEMS.find(item => item.key === key);
                if (navItem) return navItem.name;

                // Check feature config
                for (const category of Object.values(FEATURE_CONFIG)) {
                    if (category.features) {
                        const feature = category.features.find(f => f.key === key);
                        if (feature) return feature.name;
                    }
                    if (category.secondaryTabs) {
                        for (const tab of category.secondaryTabs) {
                            if (tab.master === key) return tab.name;
                            const feature = tab.features.find(f => f.key === key);
                            if (feature) return feature.name;
                        }
                    }
                }

                return key;
            };

            const featureName = getFeatureName(featureKey);

            if (error.data && error.data.message) {
                setSnackbarMessage(error.data.message);
            } else {
                setSnackbarMessage(`Error updating ${featureName} setting.`);
            }
            setSnackbarSeverity("error");
        } finally {
            setSnackbarOpen(true);
            setLoadingFor(featureKey, false);
        }
    };

    // ------------------------------------------------------------------
    // New unified data shape. Every area declares its master + groups; a
    // group either holds API-backed feature rows (kind 'api'), a reports-b
    // sub-page row (kind 'rbFeature') or a reports-b widget row (kind
    // 'rbWidget'). The render layer cares about kind so we can keep one
    // FeatureRow component for every switch.
    // ------------------------------------------------------------------
    const actualData = featureSwitchesApiData?.data || featureSwitchesApiData || {};

    // Licence tier helpers ---------------------------------------------
    // `hasDivergedFromTier(tier)` returns true if any switch in the
    // preset disagrees with what's currently saved. Drives the "your
    // changes will reset" warning before switching tiers.
    const hasDivergedFromTier = (tier) => {
        const p = LICENCE_PRESETS[tier];
        if (!p) return false;
        for (const [k, v] of Object.entries(p.api)) {
            if (Boolean(actualData[k]) !== Boolean(v)) return true;
        }
        for (const [k, v] of Object.entries(p.reportsB)) {
            if (Boolean(REPORTS_B_FEATURES[k]) !== Boolean(v)) return true;
        }
        return false;
    };

    const applyLicenceTier = async (tier) => {
        const preset = LICENCE_PRESETS[tier];
        if (!preset) return;
        try {
            // 1) API switches in one batch update
            await updateFeatureSwitches({ tenancyId, data: preset.api }).unwrap();
            // 2) reports-b feature flags via local pub-sub (Postgres-bound
            //    later via tenancy_features table)
            for (const [k, v] of Object.entries(preset.reportsB)) {
                setReportsBFeature(k, !!v);
            }
            // 3) reset reports-b widget hidden lists so widgets aren't
            //    stuck off from a prior tier. Per-widget admin tweaks
            //    can still be made afterwards from the Reports area.
            for (const page of ['summary', 'sims', 'courses']) {
                saveReportsBHidden(tenancyId, page, []);
            }
            setReportsBWidgetHidden({ summary: [], sims: [], courses: [] });
            setLicenceTier(tier);
            saveLicenceTier(tenancyId, tier);
            setSnackbarMessage(`Switched to ${preset.label} licence. Manual toggles will save normally from here.`);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (e) {
            console.error('Failed to apply licence tier', e);
            setSnackbarMessage(`Could not apply ${preset.label} licence: ${e?.data?.message || 'try again'}`);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const requestLicenceTier = (tier) => {
        if (tier === licenceTier) return;
        // Warn if user has tweaked anything off the *current* tier — they
        // would lose those tweaks by re-applying the target preset.
        if (hasDivergedFromTier(licenceTier)) {
            setPendingTier(tier);
            return;
        }
        applyLicenceTier(tier);
    };

    const buildAreas = () => {
        const summarySection = WIDGET_SECTIONS.summary || [];
        const simsSections = WIDGET_SECTIONS.sims || [];
        const coursesSections = WIDGET_SECTIONS.courses || [];

        const widgetGroup = (label, page, sections) => ({
            id: `${page}-widgets`,
            label,
            features: sections.flatMap((section) =>
                section.widgets.map((w) => ({
                    kind: 'rbWidget',
                    page,
                    key: w.id,
                    label: w.label,
                    description: w.description,
                    section: section.title,
                }))
            ),
        });

        return [
            {
                id: 'dashboard',
                label: 'Dashboard',
                description: 'Tenancy dashboard widgets shown after login.',
                icon: <DashboardIcon sx={{ fontSize: 20 }} />,
                master: 'enableDashboard',
                groups: [
                    {
                        id: 'dashboard-widgets',
                        label: 'Widgets',
                        features: [
                            { kind: 'api', key: 'enableDashboardCourseOverview',           label: 'Your Courses',            description: 'Course overview section on the dashboard.' },
                            { kind: 'api', key: 'enableDashboardActivityTimeline',         label: 'Activity Timeline',       description: 'Recent activity feed.' },
                            { kind: 'api', key: 'enableDashboardUserSearch',               label: 'User Search & Analytics', description: 'Search users and view per-user analytics.' },
                            { kind: 'api', key: 'enableDashboardMostClickedSimulations',   label: 'Most Clicked Simulations', description: 'Ranking tile of sims by click rate.' },
                            { kind: 'api', key: 'enableDashboardDepartmentOverview',       label: 'Department Overview',     description: 'Per-department engagement tile.' },
                        ],
                    },
                ],
            },
            {
                id: 'newsFeed',
                label: 'News Feed',
                description: 'Tenancy news feed nav item.',
                icon: <FeedIcon sx={{ fontSize: 20 }} />,
                master: 'enableNewsFeed',
                groups: [],
            },
            {
                id: 'content',
                label: 'Content',
                description: 'Simulations, courses, journeys and content packs.',
                icon: <PacksIcon width={20} height={20} />,
                master: 'enableContent',
                groups: [
                    {
                        id: 'content-on-demand',
                        label: 'On Demand',
                        master: 'enableOndemand',
                        features: [
                            { kind: 'api', key: 'enableSimulation',    label: 'On Demand Simulations',     description: 'Simulation features in on-demand.' },
                            { kind: 'api', key: 'enableAddSimulation', label: 'Add Simulations',          description: 'Allow creating new sims.' },
                            { kind: 'api', key: 'enableCourse',        label: 'On Demand Courses',         description: 'Course features in on-demand.' },
                        ],
                    },
                    {
                        id: 'content-journey-builder',
                        label: 'Journey Builder',
                        master: 'enableJourneyBuilder',
                        features: [
                            { kind: 'api', key: 'enableJourneyBuilderSimulations', label: 'Simulations',     description: 'Include simulations as Journey steps.' },
                            { kind: 'api', key: 'enableJourneyBuilderCourses',     label: 'Courses',          description: 'Include courses as Journey steps.' },
                        ],
                    },
                    { id: 'content-packs',          label: 'Content Packs',       master: 'enableContentPacks',     features: [] },
                    { id: 'content-random-sims',    label: 'Random Simulations',  master: 'enableRandomSimulation', features: [] },
                    { id: 'content-customer-courses', label: 'Customer Courses',  master: 'enableAddCourse',        features: [] },
                ],
            },
            {
                id: 'settings',
                label: 'Settings',
                description: 'Tenancy configuration, integrations and branding.',
                icon: <SettingsIcon width={20} height={20} />,
                master: 'enableSettings',
                groups: [
                    {
                        id: 'settings-general',
                        label: 'General',
                        master: 'enableSettingsGeneral',
                        features: [
                            { kind: 'api', key: 'enableCustomEmailHeaders',   label: 'Custom Email Headers',    description: 'Custom headers on outbound mail.' },
                            { kind: 'api', key: 'enableCustomEmailSubdomain', label: 'Custom Email Domain',     description: 'Send course mail from a custom domain.' },
                            { kind: 'api', key: 'enableReminders',            label: 'Reminders',               description: 'Email reminders for assignments.' },
                            { kind: 'api', key: 'enableLineManagerEscalation',label: 'Line Manager Escalation', description: 'CC line managers on reminders.' },
                            { kind: 'api', key: 'enablePauseJourneys',        label: 'Pause Emails',            description: 'Pause outbound email for the tenancy.' },
                            { kind: 'api', key: 'enableNotifications',        label: 'Course Notifications',    description: 'Course-notification settings section.' },
                            { kind: 'api', key: 'enableTeamsUI',              label: 'Teams Notification UI',   description: 'Show Teams toggle inside course notifications.', parentKey: 'enableNotifications' },
                            { kind: 'api', key: 'enableEmailUI',              label: 'Email Notification UI',   description: 'Show Email toggle inside course notifications.', parentKey: 'enableNotifications' },
                        ],
                    },
                    {
                        id: 'settings-deliverability',
                        label: 'Deliverability',
                        master: 'enableSettingsDeliverability',
                        features: [
                            { kind: 'api', key: 'enableSendingInfo',          label: 'Sending Information',     description: 'Show the Sending Information section.' },
                            { kind: 'api', key: 'enableMicrosoftIntegration', label: 'Microsoft Integration',   description: 'Microsoft 365 integration.' },
                            { kind: 'api', key: 'enableGoogleIntegration',    label: 'Google Integration',      description: 'Google Workspace integration.' },
                            { kind: 'api', key: 'enableGateways',             label: 'Gateways',                description: 'Gateway configuration section.' },
                        ],
                    },
                    {
                        id: 'settings-branding',
                        label: 'Branding',
                        master: 'enableSettingsBranding',
                        features: [
                            { kind: 'api', key: 'enableBrandingTheme',           label: 'Theme Customisation',     description: 'Theme colours, logo, etc.' },
                            { kind: 'api', key: 'enableBrandingEmails',          label: 'Custom Email Templates',  description: 'Branding for outbound email.' },
                            { kind: 'api', key: 'enableBrandingReportedSimEmails', label: 'Custom PRI Emails',     description: 'Branding for Phish-Report-In emails.' },
                            { kind: 'api', key: 'enableBrandingLandingPage',     label: 'Custom Landing Pages',    description: 'Branding for sim landing pages.' },
                            { kind: 'api', key: 'enableDmi',                     label: 'Direct Mail Injection',   description: 'Inject sim email via Microsoft Graph instead of SMTP.' },
                        ],
                    },
                    { id: 'settings-company-info',        label: 'Company Information', master: 'enableSettingsCompanyInfo',     features: [] },
                    { id: 'settings-phish-report-button', label: 'Phish Report Button', master: 'enableSettingsPhishReportButton', features: [] },
                    {
                        id: 'settings-api',
                        label: 'API Integration',
                        features: [
                            { kind: 'api', key: 'enableReportingApi', label: 'Reporting API', description: 'Expose reporting via API.' },
                        ],
                    },
                    { id: 'settings-teams', label: 'Teams Integration', master: 'enableTeamsIntegration', features: [] },
                ],
            },
            {
                id: 'reports',
                label: 'Reports',
                description: 'QuickSight Reports nav item.',
                icon: <ReportIcon width={20} height={20} />,
                master: 'enableReporting',
                groups: [],
            },
            {
                // Reports B is now decoupled from the QuickSight `enableReporting` flag: it has its
                // own `enableReportsB` master switch, so the two products toggle independently.
                id: 'reports-b',
                label: 'Reports B',
                description: 'Reports B nav item, sub-pages and widgets.',
                icon: <ReportIcon width={20} height={20} />,
                master: 'enableReportsB',
                groups: [
                    {
                        id: 'reports-b-pages',
                        label: 'Pages inside Reports B',
                        features: REPORTS_B_SUBPAGES.map((p) => ({
                            kind: 'rbFeature',
                            key: p.id,
                            label: p.label,
                            description: p.description,
                        })),
                    },
                    widgetGroup('Risk widgets',       'summary', summarySection),
                    widgetGroup('Simulation widgets', 'sims',    simsSections),
                    widgetGroup('Course widgets',     'courses', coursesSections),
                ],
            },
            {
                id: 'tenants',
                label: 'Tenants',
                description: 'Tenant administration nav item.',
                icon: <TenantsIcon width={20} height={20} />,
                master: 'enableTenants',
                groups: [],
            },
            {
                id: 'help',
                label: 'Help',
                description: 'Help center nav item.',
                icon: <HelpIcon width={20} height={20} />,
                master: 'enableHelp',
                groups: [],
            },
            {
                id: 'dwm-button',
                label: 'Dark Web Monitoring',
                description: 'The DWM button in the global layout.',
                icon: <TrillionIcon width={20} height={20} />,
                master: 'showTrillion',
                groups: [
                    {
                        id: 'dwm-button-state',
                        label: 'Button state',
                        features: [
                            { kind: 'api', key: 'enableTrillion', label: 'DWM button enabled', description: 'When off, the button shows but is greyed out.' },
                        ],
                    },
                ],
            },
        ];
    };

    const AREAS = buildAreas();

    // Resolve current on/off for a feature row regardless of source.
    const featureOn = (f) => {
        if (f.kind === 'rbFeature') return !!REPORTS_B_FEATURES[f.key];
        if (f.kind === 'rbWidget')  return !(reportsBWidgetHidden[f.page] || []).includes(f.key);
        return !!actualData[f.key];
    };

    const masterOn = (key) => (key ? !!actualData[key] : true);

    const toggleFeature = (f) => {
        if (f.kind === 'rbFeature') return setReportsBFeature(f.key, !REPORTS_B_FEATURES[f.key]);
        if (f.kind === 'rbWidget')  return toggleReportsBWidget(f.page, f.key);
        return handleGenericFeatureToggle(f.key);
    };

    // Match against feature label/description/key for search.
    const matchesQuery = (f) => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.trim().toLowerCase();
        return (
            (f.label || '').toLowerCase().includes(q) ||
            (f.description || '').toLowerCase().includes(q) ||
            (f.key || '').toLowerCase().includes(q)
        );
    };

    // Per-area aggregate: how many toggles and how many are on after the
    // current search filter. Drives the rail badges.
    //
    // A group's master switch is itself a toggle rendered in the UI (GroupBlock), so it must be
    // counted alongside the leaf sub-features. Counting only `g.features` meant a group whose
    // master was off — including master-only groups with no sub-features (Content Packs, Random
    // Simulations, Customer Courses) — was invisible to the badge, e.g. "5 of 5 on" while a group
    // master toggle was clearly off.
    const areaSummary = (area) => {
        const groupMasters = area.groups
            .filter((g) => g.master)
            .map((g) => ({ kind: 'api', key: g.master, label: g.label }));
        const allToggles = [...groupMasters, ...area.groups.flatMap((g) => g.features)];
        const filtered = allToggles.filter(matchesQuery);
        const onCount = filtered.filter(featureOn).length;
        return { total: filtered.length, on: onCount, masterMatches: !searchQuery.trim() || area.label.toLowerCase().includes(searchQuery.trim().toLowerCase()) };
    };

    const selectedArea = AREAS.find((a) => a.id === selectedAreaId) || AREAS[0];
    const selectedMasterOn = masterOn(selectedArea.master);
    const selectedMasterLoading = loadingStates[selectedArea.master];

    const FeatureRow = ({ f, indent = 0, parentDisabled = false }) => {
        const checked = featureOn(f);
        const disabled = parentDisabled;
        const isApi = f.kind === 'api';
        return (
            <Box sx={{
                display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 2,
                px: 2, py: 1.5,
                pl: 2 + indent * 3,
                borderBottom: '1px solid', borderColor: 'divider',
                opacity: disabled ? 0.45 : 1,
                transition: 'opacity 150ms',
                '&:last-of-type': { borderBottom: 'none' },
            }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{f.label}</Typography>
                    {f.description && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                            {f.description}
                        </Typography>
                    )}
                </Box>
                <LoadingSwitch
                    checked={!!checked}
                    onChange={() => toggleFeature(f)}
                    loading={isApi ? !!loadingStates[f.key] : false}
                    disabled={disabled}
                    inputProps={{ 'aria-label': `Toggle ${f.label}` }}
                />
            </Box>
        );
    };

    const GroupBlock = ({ group, areaMasterOn }) => {
        const groupMasterKey = group.master;
        const groupOn = groupMasterKey ? masterOn(groupMasterKey) : true;
        const groupDisabled = !areaMasterOn;
        const childDisabled = groupDisabled || (groupMasterKey ? !groupOn : false);
        const visibleFeatures = group.features.filter(matchesQuery);

        // Group with no master and no visible features: hide entirely under search.
        if (visibleFeatures.length === 0 && !groupMasterKey && searchQuery.trim()) return null;

        return (
            <MainCard content={false} border={true} boxShadow={false} sx={{ mb: 2, opacity: groupDisabled ? 0.55 : 1, transition: 'opacity 150ms' }}>
                <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{group.label}</Typography>
                        {group.master && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Master switch for this group. Off hides the whole section.
                            </Typography>
                        )}
                    </Box>
                    {group.master && (
                        <LoadingSwitch
                            checked={groupOn}
                            onChange={() => handleGenericFeatureToggle(group.master)}
                            loading={!!loadingStates[group.master]}
                            disabled={groupDisabled}
                            inputProps={{ 'aria-label': `Toggle ${group.label}` }}
                        />
                    )}
                </Box>
                {visibleFeatures.length > 0 ? (
                    <Box>
                        {visibleFeatures.map((f) => (
                            <FeatureRow key={`${group.id}-${f.key}`} f={f} parentDisabled={childDisabled || (f.parentKey ? !masterOn(f.parentKey) : false)} indent={f.parentKey ? 1 : 0} />
                        ))}
                    </Box>
                ) : (
                    group.master ? null : (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>No matching features.</Typography>
                        </Box>
                    )
                )}
            </MainCard>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Header */}
            <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Features</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    Each area on the left controls one entry in the left navigation. Its master switch turns the whole nav item on or off. Sub-features below tune what shows inside the page.
                </Typography>

                {/* Licence tier selector (essentials/enhanced) HIDDEN for now.
                    Restore by removing the `{false && (` wrapper and its closing `)}`. */}
                {false && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                    <Box>
                        <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            Licence tier
                        </Typography>
                        <Box
                            role="tablist"
                            aria-label="Licence tier"
                            sx={{
                                display: 'inline-flex',
                                p: '3px',
                                borderRadius: '8px',
                                bgcolor: 'action.hover',
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            {['platform', 'enhanced'].map((tier) => {
                                const isActive = tier === licenceTier;
                                return (
                                    <Box
                                        key={tier}
                                        role="tab"
                                        aria-selected={isActive}
                                        tabIndex={0}
                                        onClick={() => requestLicenceTier(tier)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); requestLicenceTier(tier); } }}
                                        sx={{
                                            px: 2, py: 0.75,
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: 12, fontWeight: 700,
                                            color: isActive ? 'primary.contrastText' : 'text.secondary',
                                            bgcolor: isActive ? 'primary.main' : 'transparent',
                                            transition: 'background-color 120ms, color 120ms',
                                            userSelect: 'none',
                                            '&:hover': { color: isActive ? 'primary.contrastText' : 'primary.main' },
                                            '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                                        }}
                                    >
                                        {LICENCE_PRESETS[tier].label}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
                )}

                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search every feature across the portal..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ maxWidth: 480 }}
                />
            </Box>

            {/* Confirmation dialog HIDDEN with the licence tier selector above. */}
            {false && (
            <Dialog open={!!pendingTier} onClose={() => setPendingTier(null)}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon color="warning" />
                    Reset to {pendingTier && LICENCE_PRESETS[pendingTier]?.label}?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 1 }}>
                        You've made manual changes since picking the {LICENCE_PRESETS[licenceTier].label} tier. Switching to {pendingTier && LICENCE_PRESETS[pendingTier]?.label} will overwrite those changes with the {pendingTier && LICENCE_PRESETS[pendingTier]?.label} defaults.
                    </DialogContentText>
                    <DialogContentText sx={{ fontSize: 13, color: 'text.secondary' }}>
                        You can keep tweaking individual switches after the new tier is applied — your edits save normally then.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setPendingTier(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => { const t = pendingTier; setPendingTier(null); applyLicenceTier(t); }}
                    >
                        Apply {pendingTier && LICENCE_PRESETS[pendingTier]?.label}
                    </Button>
                </DialogActions>
            </Dialog>
            )}

            {/* Body: sidebar + content */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: 2, alignItems: 'flex-start' }}>
                {/* Sidebar */}
                <MainCard content={false} boxShadow={true} border={false} sx={{ bgcolor: 'background.default', position: { md: 'sticky' }, top: { md: 16 } }}>
                    <Box sx={{ p: 1 }}>
                        {AREAS.map((area) => {
                            const { total, on, masterMatches } = areaSummary(area);
                            const isActive = area.id === selectedAreaId;
                            const isMasterOn = masterOn(area.master);
                            const hidden = searchQuery.trim() && total === 0 && !masterMatches;
                            if (hidden) return null;
                            return (
                                <Box
                                    key={area.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setSelectedAreaId(area.id)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedAreaId(area.id); } }}
                                    sx={{
                                        display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignItems: 'center', gap: 1.25,
                                        px: 1.5, py: 1.25, mb: 0.5, mx: 1,
                                        borderRadius: theme.borderRadius?.sm || 1,
                                        cursor: 'pointer',
                                        bgcolor: isActive ? theme.palette.secondary.lighter : 'transparent',
                                        transition: 'background-color 120ms ease, color 120ms ease',
                                        '&:hover': {
                                            bgcolor: theme.palette.secondary.lighter,
                                            '& .feat-rail-label': { color: 'text.primary' },
                                        },
                                        '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                                    }}
                                >
                                    <Box sx={{ color: isActive ? 'secondary.main' : (isMasterOn ? 'secondary.light' : 'text.disabled'), display: 'flex' }}>{area.icon}</Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography className="feat-rail-label" variant="body2" sx={{ fontWeight: 700, color: isActive ? 'secondary.main' : (isMasterOn ? 'text.primary' : 'text.disabled'), transition: 'color 120ms ease' }} noWrap>
                                            {area.label}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>
                                            {isMasterOn ? (total > 0 ? `${on} of ${total} on` : 'Always on') : 'Nav item hidden'}
                                        </Typography>
                                    </Box>
                                    <ChevronRightIcon fontSize="small" sx={{ color: isActive ? 'primary.main' : 'text.disabled' }} />
                                </Box>
                            );
                        })}
                    </Box>
                </MainCard>

                {/* Content panel */}
                <MainCard content={false} boxShadow={true} border={false} sx={{ bgcolor: 'background.default' }}>
                    {tenancyLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : tenancyError ? (
                        <Box sx={{ p: 3 }}>
                            <Typography color="error">Error loading tenancy data</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ p: 3 }}>
                            {/* Area header + master switch */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', minWidth: 0 }}>
                                    <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover', color: 'primary.main', display: 'flex' }}>{selectedArea.icon}</Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{selectedArea.label}</Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>{selectedArea.description}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                        Nav item
                                    </Typography>
                                    <LoadingSwitch
                                        checked={selectedMasterOn}
                                        onChange={() => handleGenericFeatureToggle(selectedArea.master)}
                                        loading={!!selectedMasterLoading}
                                        inputProps={{ 'aria-label': `Toggle ${selectedArea.label} nav item` }}
                                    />
                                    <Typography variant="caption" sx={{ color: selectedMasterOn ? 'success.main' : 'text.disabled' }}>
                                        {selectedMasterOn ? 'Visible in left nav' : 'Hidden from left nav'}
                                    </Typography>
                                </Box>
                            </Box>

                            {!selectedMasterOn && selectedArea.groups.length > 0 && (
                                <Box sx={{ p: 1.5, mb: 2, borderRadius: 1, bgcolor: 'action.hover', borderLeft: '3px solid', borderLeftColor: 'warning.main' }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        The nav item is off, so the sub-features below are dimmed. Your selections are kept and will apply when the nav item is turned back on.
                                    </Typography>
                                </Box>
                            )}

                            {/* Groups */}
                            {selectedArea.groups.length === 0 ? (
                                <Box sx={{ p: 3, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        No sub-features. The nav-item master switch above is the only control for this area.
                                    </Typography>
                                </Box>
                            ) : (
                                selectedArea.groups
                                    .filter((g) => {
                                        if (!searchQuery.trim()) return true;
                                        return g.features.some(matchesQuery) || (g.label.toLowerCase().includes(searchQuery.trim().toLowerCase()));
                                    })
                                    .map((g) => <GroupBlock key={g.id} group={g} areaMasterOn={selectedMasterOn} />)
                            )}
                        </Box>
                    )}
                </MainCard>
            </Box>

            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled"
                    sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default FeaturesDefault;